import React, { useState, useEffect } from 'react';
import { FileText, Save, Edit3 } from 'lucide-react';
import Button from '../../shared/Button';
import { useUpdateVendorProfile, useVendorProfile } from '../../../services/vendorProfileService';
import { useToastContext } from '../../../contexts/ToastContext';

const TermsConditionsTab = ({ user, updateUser }) => {
  const toast = useToastContext();
  const [isEditing, setIsEditing] = useState(false);
  
  // Fetch vendor profile data directly from API
  const { data: vendorProfileResponse, isLoading: profileLoading } = useVendorProfile();
  const vendorProfile = vendorProfileResponse?.data || user?.vendor_profile;
  
  const [termsData, setTermsData] = useState({
    terms_conditions: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (vendorProfile) {
      setTermsData({
        terms_conditions: vendorProfile.terms_conditions || ''
      });
    }
  }, [vendorProfile]);

  const updateProfileMutation = useUpdateVendorProfile({
    onSuccess: (response) => {
      toast.success('Terms & Conditions updated successfully!');
      setIsEditing(false);
      // Update local user context
      if (response.data && updateUser) {
        updateUser({
          vendor_profile: {
            ...user?.vendor_profile,
            ...response.data
          }
        });
      }
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to update terms & conditions';
      toast.error(errorMessage);
      if (error?.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
    }
  });

  const handleInputChange = (field, value) => {
    setTermsData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSave = () => {
    setErrors({});
    
    updateProfileMutation.mutate({
      terms_conditions: termsData.terms_conditions
    });
  };

  const handleCancel = () => {
    // Reset to original data
    if (vendorProfile) {
      setTermsData({
        terms_conditions: vendorProfile.terms_conditions || ''
      });
    }
    setErrors({});
    setIsEditing(false);
  };

  if (profileLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <FileText className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Terms & Conditions</h3>
            <p className="text-sm text-gray-600">Define your business terms and conditions for clients</p>
          </div>
        </div>
        
        {!isEditing && (
          <Button
            onClick={() => setIsEditing(true)}
            variant="outline"
            size="sm"
            icon={Edit3}
          >
            Edit
          </Button>
        )}
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Terms & Conditions
          </label>
          {isEditing ? (
            <textarea
              value={termsData.terms_conditions}
              onChange={(e) => handleInputChange('terms_conditions', e.target.value)}
              rows={12}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.terms_conditions ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter your terms and conditions here. This will be displayed to clients when they book your services..."
            />
          ) : (
            <div className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 min-h-[300px] whitespace-pre-wrap">
              {termsData.terms_conditions || (
                <p className="text-gray-400 italic">No terms & conditions set yet. Click Edit to add them.</p>
              )}
            </div>
          )}
          {errors.terms_conditions && (
            <p className="mt-1 text-sm text-red-600">{errors.terms_conditions[0]}</p>
          )}
          <p className="mt-2 text-xs text-gray-500">
            These terms will be shown to clients when they view your services or make bookings.
          </p>
        </div>
      </div>

      {isEditing && (
        <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button
            onClick={handleCancel}
            variant="outline"
            size="sm"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="primary"
            size="sm"
            icon={Save}
            loading={updateProfileMutation.isPending}
            disabled={updateProfileMutation.isPending}
          >
            Save Changes
          </Button>
        </div>
      )}
    </div>
  );
};

export default TermsConditionsTab;

